import { fetchAuthSession } from 'aws-amplify/auth';

import { queryClient } from '../components/queryClient';
import { HaygentMessage, HaygentSession, HaygentStreamChunk } from '../interfaces';
import { sleep } from '../utils/sleep';

// Enable mock mode via URL param: &mock_haygent=1 (use & if URL already has ?)
const isMockMode = () => new URLSearchParams(window.location.search).has('mock_haygent');

// Cache for mock responses loaded from public directory
let mockResponsesCache: string[] | null = null;

const loadMockResponses = async (): Promise<string[]> => {
  if (mockResponsesCache) {
    return mockResponsesCache;
  }

  const response = await fetch('/haygent-mock-responses.json');
  const data: string[] = await response.json();
  mockResponsesCache = data;

  return data;
};

export abstract class HaygentService {
  private static readonly baseUrl = 'https://haygent.test.console.hayden.ai/api/v1';

  /**
   * Gets the OIDC ID token from Cognito session.
   * Note: Uses idToken (not accessToken) as required by the Haygent backend.
   */
  private static getIdToken = async (): Promise<string> => {
    const session = await fetchAuthSession();

    const idToken = session?.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error('No authentication token');
    }

    return idToken;
  };

  /**
   * Builds common headers with auth and optional impersonation.
   */
  private static buildHeaders = async (extra: Record<string, string> = {}): Promise<Record<string, string>> => {
    const token = await HaygentService.getIdToken();
    const impersonatedUser = queryClient.getQueryData(['impersonatedUser']) as string | null;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...extra,
    };

    if (impersonatedUser) {
      headers['X-Authenticated-As'] = impersonatedUser;
    }

    return headers;
  };

  /**
   * Creates a new chat session
   */
  public static createSession = async (): Promise<HaygentSession> => {
    if (isMockMode()) {
      await sleep(500);

      return {
        session_id: `mock-session-${Date.now()}`,
        name: '',
      };
    }

    const response = await fetch(`${HaygentService.baseUrl}/session`, {
      method: 'POST',
      headers: await HaygentService.buildHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.status}`);
    }

    return response.json();
  };

  /**
   * Sends a message and streams the response via SSE
   */
  public static streamChat = async (
    sessionId: string,
    messages: HaygentMessage[],
    onChunk: (chunk: HaygentStreamChunk) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> => {
    if (isMockMode()) {
      await HaygentService.mockStreamChat(messages, onChunk, abortSignal);

      return;
    }

    const response = await fetch(`${HaygentService.baseUrl}/chatbot/chat/stream`, {
      method: 'POST',
      headers: await HaygentService.buildHeaders({
        'X-Session-Id': sessionId,
        Accept: 'text/event-stream',
      }),
      body: JSON.stringify({ messages }),
      signal: abortSignal,
    });

    if (!response.ok) {
      const error = new Error(`Chat request failed: ${response.status}`) as Error & { status: number };
      error.status = response.status;
      throw error;
    }

    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamComplete = false;

    const processChunks = async (): Promise<void> => {
      if (streamComplete) {
        return;
      }

      const { value, done } = await reader.read();
      if (done) {
        return;
      }

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() || '';

      parts.forEach((part) => {
        const line = part.split('\n').find((l) => l.startsWith('data:'));
        if (line) {
          try {
            const payload: HaygentStreamChunk = JSON.parse(line.replace(/^data:\s?/, ''));
            onChunk(payload);
            if (payload.done) {
              streamComplete = true;
              // Proactively close the stream to avoid leaving the SSE connection open
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              reader.cancel();
            }
          } catch {
            // Skip malformed JSON
          }
        }
      });

      if (!streamComplete) {
        await processChunks();
      }
    };

    await processChunks();
  };

  /**
   * Mock streaming chat for testing UI without backend
   */
  private static mockStreamChat = async (
    messages: HaygentMessage[],
    onChunk: (chunk: HaygentStreamChunk) => void,
    abortSignal?: AbortSignal,
  ): Promise<void> => {
    const mockResponses = await loadMockResponses();

    // Pick a response based on message count or randomly
    const responseIndex = (messages.length - 1) % mockResponses.length;
    const fullResponse = mockResponses[responseIndex];

    // Simulate streaming by sending chunks with delays
    const words = fullResponse.split(' ');
    const chunkSize = 3; // words per chunk

    for (let i = 0; i < words.length; i += chunkSize) {
      if (abortSignal?.aborted) {
        return;
      }

      const chunk = words.slice(i, i + chunkSize).join(' ') + (i + chunkSize < words.length ? ' ' : '');
      // eslint-disable-next-line no-await-in-loop
      await sleep(50 + Math.random() * 100); // 50-150ms between chunks

      onChunk({ content: chunk, done: false });
    }

    onChunk({ content: '', done: true });
  };
}
